import { defineComponent as _defineComponent } from 'vue'
import { Offer } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import ListPromotionRow from '@/features/buybox/components/list/ListPromotionRow.vue';
import { computed, ref } from 'vue';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import FreeTrialSwiperOSContainer from './FreeTrialSwiperOSContainer.vue';
import { freeTrialSwiperRules, SwiperRule } from '@/helpers/promotion-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { UsePromotion } from '@/helpers/composables/usePromotion';
import { OfferLocation } from '@/helpers/offer-helper';
import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { useSwiperBrowserExp } from '@/components/experiments/SwiperBrowserExp';
import { useUserAgent } from '@/helpers/composables/useUserAgent';
import { useExperiment } from '@/libraries/experiment';
import { SnowplowContext } from '@/helpers/tracking/providers';

interface ClickoutPayload {
	buyboxOffer: UsePromotion['promotionOffer'];
	offerLocation: OfferLocation.FREE_TRIAL;
	row: HTMLElement | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FreeTrialSwiperOS',
  props: {
    offers: null,
    title: null,
    additionalContexts: null
  },
  emits: ["clickOut"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'clickOut', payload: ClickoutPayload): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	offers: Offer[];
	title: TitleDetail;
	additionalContexts: SnowplowContext[];
};





const { country } = useLanguageStore();

const { swiperBrowserConfig } = useSwiperBrowserExp();
const { userBrowser } = useUserAgent();
const { activeVariantWithControlGroup: swiperBrowserExpVariant } = useExperiment('SWIPER_BROWSER_EXP_2');

const { geoLocation } = useGeoSwiper({ title: () => props.title });

const offersForWebLocale = computed(() => props.offers.filter(offer => offer.country === country.value));

const freeTrialSwiperActive = computed(() => {
	// Swiper Browser Experiment
	if (!!swiperBrowserConfig.value) {
		return true;
	}
	// Swiper Browser Experiment

	return freeTrialSwiperRules[country.value] != null;
});

const freeTrialSwiperConfig = computed<SwiperRule | null>(() => {
	// Swiper Browser Experiment
	if (swiperBrowserConfig.value) {
		return swiperBrowserConfig.value;
	}
	// Swiper Browser Experiment

	return freeTrialSwiperRules[country.value] ?? null;
});

const swiperPackages = computed(() => {
	const allPackages = freeTrialSwiperConfig.value?.packages || [];

	return allPackages.map(pkg => {
		const hasMatchingGeoLocation = freeTrialSwiperConfig.value?.rules.find(
			rule => rule.id === pkg.id && rule.selectors.geoLocations.includes(geoLocation.value || '')
		);

		return {
			...pkg,
			// Swiper Browser Experiment
			activeGeoLocation: hasMatchingGeoLocation && !swiperBrowserExpVariant.value ? geoLocation.value : null,
			// Swiper Browser Experiment
		};
	});
});

const swipeIndex = ref(0);

const isShowOrSeason = computed(() => ['Show', 'Season'].includes(props.title.__typename));

const slideDelay = computed(() => freeTrialSwiperConfig.value?.slideDelay ?? 1500);

const slideToPackageId = computed(() => {
	if (freeTrialSwiperConfig.value == null) return null;

	/**
	 * Currently we only have either GeoLocation or Browser selector per rule, but for future-proofing:
	 * 	- GeoLocation selector has priority over Browser selector within the same rule
	 * 	- Selector rules have priority by order of appearance in the config file
	 */
	const ruleWithMatchingSelector = freeTrialSwiperConfig.value.rules.find(rule => {
		// Swiper Browser Experiment
		if (!!swiperBrowserExpVariant.value) {
			return rule.selectors.geoLocations.includes(geoLocation.value || '') && userBrowser.value !== 'safari';
		}
		// Swiper Browser Experiment
		return (
			rule.selectors.geoLocations[0] === geoLocation.value || rule.selectors.browsers?.includes(userBrowser.value)
		);
	});

	return ruleWithMatchingSelector ? ruleWithMatchingSelector.id : freeTrialSwiperConfig.value.default.id;
});

const swiperSlideToIndex = computed(() => {
	if (swiperPackages.value == null || slideToPackageId.value == null) return 0;

	return Math.max(
		0,
		swiperPackages.value.findIndex(el => el.id === slideToPackageId.value)
	);
});

function handleClickout(payload: ClickoutPayload, geoCountry: string | undefined, index: number) {
	const countryOrGeoCountry = geoCountry ?? country.value;

	TrackingHelper.trackEvent('userinteraction', {
		action: 'click',
		label: `free_trial_${countryOrGeoCountry}`,
		property: 'geo_swiper',
		value: index,
	});

	emit('clickOut', payload);
}

function slideChange({
	direction,
	position,
	skipTracking,
	nonInteraction,
}: {
	direction: string;
	position: number;
	skipTracking: boolean;
	nonInteraction: boolean;
}) {
	/**
	 * Tiny Swiper bug workaround:
	 * The library can return NaN for the position, and this creates bad rows for BI.
	 */
	const parsedInitialPosition = Number(swipeIndex.value);
	const initialPosition = isNaN(parsedInitialPosition) ? undefined : parsedInitialPosition;

	const category = nonInteraction ? 'automation' : 'userinteraction';
	const action = nonInteraction ? 'auto_swipe' : 'swipe_clicked';

	swipeIndex.value = position;

	TrackingHelper.trackEvent(category, {
		action,
		label: direction,
		nonInteraction,
		value: initialPosition,
		property: 'geo_swiper',
	});
}

return { __sfc: true,props, emit, country, swiperBrowserConfig, userBrowser, swiperBrowserExpVariant, geoLocation, offersForWebLocale, freeTrialSwiperActive, freeTrialSwiperConfig, swiperPackages, swipeIndex, isShowOrSeason, slideDelay, slideToPackageId, swiperSlideToIndex, handleClickout, slideChange, ListPromotionRow, FreeTrialSwiperOSContainer }
}

})